import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import api from '../../services/api';
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const Title = styled.h2`
  color: #fff;
  font-size: 24px;
  margin: 0;
`;

const AddButton = styled.button`
  padding: 10px 20px;
  background: #4caf50;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #45a049;
    transform: translateY(-2px);
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
  
  h3 {
    color: #999;
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 10px 0;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  p {
    color: #fff;
    font-size: 28px;
    font-weight: 700;
    margin: 0;
  }
  
  span {
    color: #4caf50;
    font-size: 14px;
    font-weight: 600;
  }
`;

const FilterContainer = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  display: flex;
  gap: 15px;
  align-items: end;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  label {
    color: #999;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  input {
    padding: 10px 12px;
    background: #1a1a1a;
    border: 1px solid #333;
    border-radius: 6px;
    color: #fff;
    font-size: 14px;
    
    &:focus {
      outline: none;
      border-color: #4caf50;
    }
  }
`;

const FilterButton = styled.button`
  padding: 10px 20px;
  background: #4caf50;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #45a049;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ClearButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #333;
  border-radius: 6px;
  color: #999;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #666;
    color: #fff;
  }
`;

const Table = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 12px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr 1fr 1fr 1fr 0.8fr;
  gap: 20px;
  padding: 16px;
  background: #1a1a1a;
  border-bottom: 1px solid #333;
  font-weight: 600;
  color: #999;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr 0.8fr;

    & > :nth-child(3),
    & > :nth-child(4) {
      display: none;
    }
  }
`;

const TableRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1.5fr 1fr 1fr 1fr 0.8fr;
  gap: 20px;
  padding: 16px;
  border-bottom: 1px solid #333;
  align-items: center;
  transition: all 0.3s ease;

  &:hover {
    background: #1a1a1a;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr 0.8fr;

    & > :nth-child(3),
    & > :nth-child(4) {
      display: none;
    }
  }
`;

const OrderId = styled.p`
  color: #fff;
  font-weight: 600;
  margin: 0;
  font-size: 14px;
`;

const CustomerName = styled.p`
  color: #fff;
  margin: 0;
  font-size: 14px;
`;

const Status = styled.span`
  padding: 6px 12px;
  background: ${props => {
    switch (props.value) {
      case 'Completed':
        return 'rgba(76, 175, 80, 0.2)';
      case 'In Progress':
        return 'rgba(255, 193, 7, 0.2)';
      case 'Pending':
        return 'rgba(244, 67, 54, 0.2)';
      case 'Picked Up':
        return 'rgba(33, 150, 243, 0.2)';
      case 'Delivered':
        return 'rgba(76, 175, 80, 0.2)';
      default:
        return 'rgba(100, 100, 100, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.value) {
      case 'Completed':
        return '#4caf50';
      case 'In Progress':
        return '#ffc107';
      case 'Pending':
        return '#f44336';
      case 'Picked Up':
        return '#2196f3';
      case 'Delivered':
        return '#4caf50';
      default:
        return '#999';
    }
  }};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  width: fit-content;
`;

const Amount = styled.p`
  color: #fff;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
`;

const Date = styled.p`
  color: #999;
  margin: 0;
  font-size: 12px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  padding: 6px 10px;
  background: none;
  border: 1px solid #333;
  border-radius: 4px;
  color: #ff6b35;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: #ff6b35;
    color: #fff;
    border-color: #ff6b35;
  }
`;

const DeleteButton = styled(ActionButton)`
  color: #f44336;

  &:hover {
    background: #f44336;
  }
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: #999;
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 30px;
  max-width: 500px;
  width: 90%;
`;

const ModalTitle = styled.h3`
  color: #fff;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  color: #999;
  font-size: 12px;
  text-transform: uppercase;
  display: block;
  margin-bottom: 6px;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  color: #fff;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  color: #fff;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 10px;
  background: #4caf50;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #45a049;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 10px;
  background: #666;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #555;
  }
`;

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);
  const [newOrderData, setNewOrderData] = useState({
    customerName: '',
    customerPhone: '',
    pickupAddress: '',
    notes: ''
  });
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemQuantities, setItemQuantities] = useState({});
  const [customerMembership, setCustomerMembership] = useState(null);
  const [checkingMembership, setCheckingMembership] = useState(false);
  const [viewingOrderItems, setViewingOrderItems] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [searchFilter, setSearchFilter] = useState('');

  useEffect(() => {
    fetchOrders();
    fetchAllItems();
  }, []);

  const fetchAllItems = async () => {
    try {
      const response = await api.getItems();
      if (response.success && response.data) {
        setAvailableItems(response.data);
      }
    } catch (err) {
      console.error('Error fetching items:', err);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await api.adminGetOrders();
      if (response.success && response.data) {
        setOrders(response.data);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setLoading(false);
    }
  };

  const getFilteredOrders = () => {
    let filtered = orders;
    
    // Apply search filter (order number or order ID)
    if (searchFilter.trim()) {
      const searchLower = searchFilter.toLowerCase().trim();
      filtered = filtered.filter(order => {
        const orderNumber = (order.order_number || '').toLowerCase();
        const orderId = (order.id || '').toString();
        return orderNumber.includes(searchLower) || orderId.includes(searchLower);
      });
    }
    
    // Apply date filter
    if (!dateFilter.startDate && !dateFilter.endDate) {
      return filtered;
    }
    
    return filtered.filter(order => {
      const orderDate = order.created_at ? new window.Date(order.created_at) : (order.createdAt?.toDate?.() || null);
      if (!orderDate) return false;
      
      const start = dateFilter.startDate ? new window.Date(dateFilter.startDate) : null;
      const end = dateFilter.endDate ? new window.Date(dateFilter.endDate) : null;
      
      if (start && end) {
        end.setHours(23, 59, 59, 999);
        return orderDate >= start && orderDate <= end;
      } else if (start) {
        return orderDate >= start;
      } else if (end) {
        end.setHours(23, 59, 59, 999);
        return orderDate <= end;
      }
      
      return true;
    });
  };

  const getTodayStats = () => {
    const today = new window.Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new window.Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const todayOrders = orders.filter(order => {
      const orderDate = order.created_at ? new window.Date(order.created_at) : (order.createdAt?.toDate?.() || null);
      return orderDate && orderDate >= today && orderDate < tomorrow;
    });
    
    const todayTotal = todayOrders.reduce((sum, order) => sum + (parseFloat(order.final_amount || order.totalAmount) || 0), 0);
    
    return {
      count: todayOrders.length,
      total: todayTotal
    };
  };

  const getFilteredStats = () => {
    const filtered = getFilteredOrders();
    const total = filtered.reduce((sum, order) => sum + (parseFloat(order.final_amount || order.totalAmount) || 0), 0);
    
    return {
      count: filtered.length,
      total: total
    };
  };

  const handleApplyFilter = () => {
    // Filter is applied automatically via getFilteredOrders
  };

  const handleClearFilter = () => {
    setDateFilter({ startDate: '', endDate: '' });
    setSearchFilter('');
  };

  const handleEditStatus = async () => {
    try {
      const response = await api.adminUpdateOrderStatus(editingOrder.id, editStatus);
      if (response.success) {
        setOrders(orders.map(o => 
          o.id === editingOrder.id ? { ...o, status: editStatus } : o
        ));
        setEditingOrder(null);
      }
    } catch (err) {
      console.error('Error updating order:', err);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        const response = await api.adminDeleteOrder(orderId);
        if (response.success) {
          setOrders(orders.filter(o => o.id !== orderId));
        }
      } catch (err) {
        console.error('Error deleting order:', err);
      }
    }
  };

  const handleViewItems = async (orderId) => {
    try {
      const response = await api.adminGetOrderItems(orderId);
      if (response.success) {
        setOrderItems(response.data || []);
        setViewingOrderItems(orderId);
      }
    } catch (err) {
      console.error('Error fetching order items:', err);
      alert('Failed to load order items');
    }
  };

  const handleAddItem = (itemId) => {
    if (!selectedItems.includes(itemId)) {
      setSelectedItems([...selectedItems, itemId]);
      setItemQuantities({ ...itemQuantities, [itemId]: 1 });
    }
  };

  const handleRemoveItem = (itemId) => {
    setSelectedItems(selectedItems.filter(id => id !== itemId));
    const newQuantities = { ...itemQuantities };
    delete newQuantities[itemId];
    setItemQuantities(newQuantities);
  };

  const handleQuantityChange = (itemId, quantity) => {
    if (quantity > 0) {
      setItemQuantities({ ...itemQuantities, [itemId]: quantity });
    }
  };

  const checkCustomerMembership = async (phone) => {
    if (!phone || phone.length < 10) {
      setCustomerMembership(null);
      return;
    }

    setCheckingMembership(true);
    try {
      const response = await api.request(`/admin/customers/membership-check?phone=${phone}`);
      if (response.success && response.data) {
        setCustomerMembership(response.data);
      } else {
        setCustomerMembership(null);
      }
    } catch (err) {
      setCustomerMembership(null);
    } finally {
      setCheckingMembership(false);
    }
  };

  const calculateOrderTotal = () => {
    const totalItems = selectedItems.reduce((sum, itemId) => {
      return sum + (itemQuantities[itemId] || 0);
    }, 0);

    let regularTotal = selectedItems.reduce((total, itemId) => {
      const item = availableItems.find(i => i.id === itemId);
      const quantity = itemQuantities[itemId] || 0;
      const price = item ? item.price : 0;
      return total + (price * quantity);
    }, 0);

    // Calculate membership discount for daily wear items
    let membershipDiscount = 0;
    let dailyWearCount = 0;
    
    if (customerMembership && customerMembership.clothes_remaining > 0) {
      selectedItems.forEach(itemId => {
        const item = availableItems.find(i => i.id === itemId);
        const quantity = itemQuantities[itemId] || 0;
        
        if (item && item.service_type === 'steam_iron' && item.subcategory === 'Daily Wear') {
          dailyWearCount += quantity;
        }
      });

      const membershipCoverage = Math.min(dailyWearCount, customerMembership.clothes_remaining);
      
      if (membershipCoverage > 0) {
        selectedItems.forEach(itemId => {
          const item = availableItems.find(i => i.id === itemId);
          const quantity = itemQuantities[itemId] || 0;
          
          if (item && item.service_type === 'steam_iron' && item.subcategory === 'Daily Wear') {
            const price = item.price;
            const itemDiscount = (membershipCoverage / dailyWearCount) * (price * quantity);
            membershipDiscount += itemDiscount;
          }
        });
      }
    }

    return {
      total: regularTotal,
      discount: membershipDiscount,
      final: regularTotal - membershipDiscount,
      totalItems: totalItems,
      dailyWearCount: dailyWearCount,
      membershipCoverage: customerMembership ? Math.min(dailyWearCount, customerMembership.clothes_remaining) : 0
    };
  };

  const handleCreateWalkInOrder = async () => {
    if (!newOrderData.customerName || !newOrderData.customerPhone) {
      alert('Customer name and phone are required');
      return;
    }

    if (selectedItems.length === 0) {
      alert('Please select at least one item');
      return;
    }

    try {
      const orderItems = selectedItems.map(itemId => {
        const item = availableItems.find(i => i.id === itemId);
        const price = item.price;
        return {
          item_id: itemId,
          item_type: item.service_type,
          item_name: item.item_name,
          quantity: itemQuantities[itemId],
          price_per_item: price,
          original_price: price
        };
      });

      const orderData = {
        ...newOrderData,
        items: orderItems,
        total_amount: calculateOrderTotal()
      };

      const response = await api.adminCreateWalkInOrder(orderData);
      if (response.success) {
        alert(response.message || 'Order created successfully');
        setShowNewOrderModal(false);
        setCustomerMembership(null);
        setNewOrderData({
          customerName: '',
          customerPhone: '',
          pickupAddress: '',
          notes: ''
        });
        setSelectedServiceType('');
        setSelectedItems([]);
        setItemQuantities({});
        fetchOrders();
      }
    } catch (err) {
      console.error('Error creating walk-in order:', err);
      alert('Failed to create order. Please try again.');
    }
  };

  if (loading) {
    return <Container>Loading orders...</Container>;
  }

  const todayStats = getTodayStats();
  const filteredStats = getFilteredStats();
  const filteredOrders = getFilteredOrders();

  return (
    <Container>
      <Header>
        <Title>Orders Management</Title>
        <AddButton onClick={() => {
          setShowNewOrderModal(true);
          setNewOrderData({
            customerName: '',
            customerPhone: '',
            pickupAddress: '',
            notes: ''
          });
          setSelectedServiceType('');
          setSelectedItems([]);
          setItemQuantities({});
          setCustomerMembership(null);
        }}>
          <FiPlus /> New Order
        </AddButton>
      </Header>

      <StatsContainer>
        <StatCard>
          <h3>Today's Orders</h3>
          <p>{todayStats.count}</p>
          <span>₹{todayStats.total.toFixed(2)}</span>
        </StatCard>
        <StatCard>
          <h3>Total Orders</h3>
          <p>{orders.length}</p>
        </StatCard>
        {(dateFilter.startDate || dateFilter.endDate) && (
          <StatCard>
            <h3>Filtered Results</h3>
            <p>{filteredStats.count}</p>
            <span>₹{filteredStats.total.toFixed(2)}</span>
          </StatCard>
        )}
      </StatsContainer>

      <FilterContainer>
        <FilterGroup style={{ flex: 1, minWidth: '200px' }}>
          <label>Search Order</label>
          <input
            type="text"
            placeholder="Order Number or ID..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
        </FilterGroup>
        <FilterGroup>
          <label>Start Date</label>
          <input
            type="date"
            value={dateFilter.startDate}
            onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
          />
        </FilterGroup>
        <FilterGroup>
          <label>End Date</label>
          <input
            type="date"
            value={dateFilter.endDate}
            onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
          />
        </FilterGroup>
        <FilterButton onClick={handleApplyFilter} disabled={!dateFilter.startDate && !dateFilter.endDate}>
          Apply Filter
        </FilterButton>
        <ClearButton onClick={handleClearFilter}>
          Clear
        </ClearButton>
      </FilterContainer>

      {filteredOrders.length > 0 ? (
        <Table>
          <TableHeader>
            <div>Order ID</div>
            <div>Customer</div>
            <div>Status</div>
            <div>Amount</div>
            <div>Date</div>
            <div>Actions</div>
          </TableHeader>
          {filteredOrders.map((order, index) => (
            <TableRow
              key={order.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <OrderId>#{order.order_number || String(order.id).slice(0, 8)}</OrderId>
              <CustomerName>{order.customerName || 'N/A'}</CustomerName>
              <Status value={order.status}>{order.status}</Status>
              <Amount>₹{order.final_amount || order.totalAmount || 0}</Amount>
              <Date>
                {order.created_at ? (new window.Date(order.created_at)).toLocaleDateString() : (order.createdAt?.toDate?.().toLocaleDateString?.() || 'N/A')}
              </Date>
              <Actions>
                <ActionButton onClick={() => handleViewItems(order.id)}>
                  <FiEdit2 /> View Items
                </ActionButton>
                <ActionButton onClick={() => {
                  setEditingOrder(order);
                  setEditStatus(order.status);
                }}>
                  <FiEdit2 /> Edit
                </ActionButton>
                <DeleteButton onClick={() => handleDeleteOrder(order.id)}>
                  <FiTrash2 /> Delete
                </DeleteButton>
              </Actions>
            </TableRow>
          ))}
        </Table>
      ) : (
        <EmptyState>No orders found</EmptyState>
      )}

      {showNewOrderModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowNewOrderModal(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto', maxWidth: '750px', padding: '30px' }}>
            <ModalTitle style={{ fontSize: '24px', marginBottom: '25px', borderBottom: '2px solid #d4af37', paddingBottom: '15px' }}>Create Walk-In Order</ModalTitle>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <FormGroup style={{ marginBottom: 0 }}>
                <Label>Customer Name *</Label>
                <Input
                  type="text"
                  value={newOrderData.customerName}
                  onChange={(e) => setNewOrderData({...newOrderData, customerName: e.target.value})}
                  placeholder="Enter name"
                />
              </FormGroup>
              
              <FormGroup style={{ marginBottom: 0 }}>
                <Label>Customer Phone *</Label>
                <Input
                  type="tel"
                  value={newOrderData.customerPhone}
                  onChange={(e) => {
                    const phone = e.target.value;
                    setNewOrderData({...newOrderData, customerPhone: phone});
                    if (phone.length >= 10) {
                      checkCustomerMembership(phone);
                    } else {
                      setCustomerMembership(null);
                    }
                  }}
                  placeholder="Enter phone"
                />
              </FormGroup>
            </div>

            {checkingMembership && (
              <div style={{ color: '#d4af37', fontSize: '13px', marginBottom: '15px', padding: '12px', background: '#2a2a2a', borderRadius: '8px', border: '1px solid #d4af37' }}>⏳ Checking membership...</div>
            )}
            {customerMembership && customerMembership.clothes_remaining > 0 && (
              <div style={{ background: 'linear-gradient(135deg, #1b4d1b 0%, #0d3d0d 100%)', padding: '16px', borderRadius: '8px', marginBottom: '20px', border: '2px solid #4caf50', boxShadow: '0 2px 8px rgba(76, 175, 80, 0.2)' }}>
                <div style={{ color: '#4caf50', fontSize: '15px', fontWeight: 'bold', marginBottom: '6px' }}>✓ Active Membership: {customerMembership.plan_name}</div>
                <div style={{ color: '#a5d6a7', fontSize: '13px' }}>{customerMembership.clothes_remaining} clothes remaining • Daily wear items FREE</div>
              </div>
            )}
            
            <FormGroup style={{ marginBottom: '20px' }}>
              <Label>Pickup Address (Optional)</Label>
              <Input
                type="text"
                value={newOrderData.pickupAddress}
                onChange={(e) => setNewOrderData({...newOrderData, pickupAddress: e.target.value})}
                placeholder="Enter address (optional)"
                style={{ padding: '12px' }}
              />
            </FormGroup>

            <FormGroup style={{ marginBottom: '20px' }}>
              <Label style={{ fontSize: '15px', fontWeight: '600' }}>Service Type *</Label>
              <Select
                value={selectedServiceType}
                onChange={(e) => setSelectedServiceType(e.target.value)}
                style={{ padding: '12px', fontSize: '14px' }}
              >
                <option value="">Select service type</option>
                <option value="steam_iron">Steam Iron</option>
                <option value="dry_clean">Dry Clean</option>
                <option value="laundry">Laundry</option>
                <option value="household">Household</option>
              </Select>
            </FormGroup>

            {selectedServiceType && (
              <FormGroup style={{ marginBottom: '20px' }}>
                <Label style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px' }}>Select Items *</Label>
                <div style={{ maxHeight: '280px', overflowY: 'auto', border: '2px solid #333', borderRadius: '8px', padding: '12px', background: '#1a1a1a' }}>
                  {availableItems
                    .filter(item => item.service_type === selectedServiceType)
                    .map(item => (
                      <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px', marginBottom: '8px', background: '#252525', borderRadius: '6px', border: '1px solid #444', transition: 'all 0.2s', cursor: 'pointer' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: '#fff', fontWeight: '600', fontSize: '15px' }}>{item.item_name}</div>
                          <div style={{ marginTop: '4px' }}>
                            <span style={{ color: '#d4af37', fontSize: '14px', fontWeight: '700' }}>₹{item.price}</span>
                          </div>
                        </div>
                        {selectedItems.includes(item.id) ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Input
                              type="number"
                              min="1"
                              value={itemQuantities[item.id] || 1}
                              onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value))}
                              style={{ width: '50px', padding: '6px', fontSize: '13px' }}
                            />
                            <CancelButton onClick={() => handleRemoveItem(item.id)} style={{ padding: '6px 12px', fontSize: '12px' }}>✕</CancelButton>
                          </div>
                        ) : (
                          <SaveButton onClick={() => handleAddItem(item.id)} style={{ padding: '6px 16px', fontSize: '12px' }}>+ Add</SaveButton>
                        )}
                      </div>
                    ))}
                </div>
              </FormGroup>
            )}

            {selectedItems.length > 0 && (
              <FormGroup style={{ marginBottom: '20px' }}>
                <Label style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px' }}>Order Summary ({selectedItems.length} {selectedItems.length === 1 ? 'item' : 'items'})</Label>
                <div style={{ background: '#1a1a1a', padding: '16px', borderRadius: '8px', border: '2px solid #333' }}>
                  {selectedItems.map(itemId => {
                    const item = availableItems.find(i => i.id === itemId);
                    const quantity = itemQuantities[itemId] || 0;
                    const isDailyWear = item && item.service_type === 'steam_iron' && item.subcategory === 'Daily Wear';
                    const price = item ? item.price : 0;
                    return (
                      <div key={itemId} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', color: '#fff', fontSize: '13px' }}>
                        <span>
                          {item.item_name} × {quantity}
                          {isDailyWear && customerMembership && customerMembership.clothes_remaining > 0 && <span style={{ color: '#4caf50', fontSize: '11px', marginLeft: '5px' }}>👕</span>}
                        </span>
                        <span style={{ fontWeight: '500' }}>₹{(price * quantity).toFixed(2)}</span>
                      </div>
                    );
                  })}
                  {(() => {
                    const totals = calculateOrderTotal();
                    return (
                      <>
                        <div style={{ borderTop: '1px solid #333', marginTop: '8px', paddingTop: '8px' }}>
                          {totals.discount > 0 && (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#aaa', fontSize: '13px' }}>
                                <span>Subtotal ({totals.totalItems} items):</span>
                                <span>₹{totals.total.toFixed(2)}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#4caf50', fontSize: '13px', fontWeight: '600' }}>
                                <span>Membership ({totals.membershipCoverage} items FREE):</span>
                                <span>-₹{totals.discount.toFixed(2)}</span>
                              </div>
                            </>
                          )}
                        </div>
                        <div style={{ borderTop: '2px solid #d4af37', marginTop: '8px', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#d4af37', fontSize: '14px', fontWeight: '600' }}>TOTAL TO PAY:</span>
                          <span style={{ color: '#d4af37', fontSize: '22px', fontWeight: 'bold' }}>₹{totals.final.toFixed(2)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </FormGroup>
            )}
            
            <FormGroup>
              <Label>Notes (Optional)</Label>
              <Input
                type="text"
                value={newOrderData.notes}
                onChange={(e) => setNewOrderData({...newOrderData, notes: e.target.value})}
                placeholder="Any special instructions"
              />
            </FormGroup>
            
            <ButtonGroup>
              <SaveButton onClick={handleCreateWalkInOrder}>
                Create Order (₹{calculateOrderTotal().final.toFixed(2)})
              </SaveButton>
              <CancelButton onClick={() => {
                setShowNewOrderModal(false);
                setSelectedServiceType('');
                setSelectedItems([]);
                setItemQuantities({});
              }}>Cancel</CancelButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {viewingOrderItems && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setViewingOrderItems(null)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Order Items</ModalTitle>
            {orderItems.length > 0 ? (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {orderItems.map((item, index) => (
                  <div key={index} style={{ padding: '12px', borderBottom: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ color: '#fff', fontWeight: '600', marginBottom: '4px' }}>{item.item_name}</div>
                      <div style={{ color: '#999', fontSize: '14px' }}>Quantity: {item.quantity}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#d4af37', fontWeight: '600' }}>₹{item.price_per_item}</div>
                      <div style={{ color: '#999', fontSize: '14px' }}>Total: ₹{(item.price_per_item * item.quantity).toFixed(2)}</div>
                    </div>
                  </div>
                ))}
                <div style={{ padding: '15px', background: '#1a1a1a', marginTop: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#999', fontSize: '14px' }}>
                    <span>Subtotal:</span>
                    <span>₹{orderItems.reduce((sum, item) => sum + (item.price_per_item * item.quantity), 0).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '18px', paddingTop: '8px', borderTop: '1px solid #333' }}>
                    <span style={{ color: '#fff' }}>Order Total:</span>
                    <span style={{ color: '#d4af37' }}>₹{orderItems.reduce((sum, item) => sum + (item.price_per_item * item.quantity), 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>No items found for this order</div>
            )}
            <ButtonGroup>
              <CancelButton onClick={() => setViewingOrderItems(null)}>Close</CancelButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {editingOrder && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setEditingOrder(null)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Update Order Status</ModalTitle>
            <FormGroup>
              <Label>Order ID</Label>
              <Input type="text" value={editingOrder.order_number || String(editingOrder.id).slice(0, 8)} disabled />
            </FormGroup>
            <FormGroup>
              <Label>Status</Label>
              <Select value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="picked_up">Picked Up</option>
                <option value="processing">Processing</option>
                <option value="ready">Ready</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </FormGroup>
            <ButtonGroup>
              <SaveButton onClick={handleEditStatus}>Save</SaveButton>
              <CancelButton onClick={() => setEditingOrder(null)}>Cancel</CancelButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default AdminOrders;
